import axios from 'axios';
import { waitOutRestarts } from '../../services/restartRetry';

// Own base URL - the outreach service is a separate container/DB, even
// though it shares auth with services/api.js's backend.
const OUTREACH_API_URL = process.env.REACT_APP_OUTREACH_API_URL || 'http://localhost:8100';

class OutreachApiService {
  constructor() {
    this.client = axios.create({
      baseURL: OUTREACH_API_URL,
      headers: { 'Content-Type': 'application/json' },
    });
    waitOutRestarts(this.client);
  }

  setToken(token) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.common['Authorization'];
    }
  }

  async health() {
    const response = await this.client.get('/api/outreach/health');
    return response.data;
  }

  async me() {
    const response = await this.client.get('/api/outreach/me');
    return response.data;
  }

  async features() {
    const response = await this.client.get('/api/outreach/features');
    return response.data;
  }

  async updateAccountLimits(accountId, { dailyCap, minGapSeconds, jitterSeconds }) {
    const response = await this.client.patch(`/api/outreach/accounts/${accountId}/limits`, {
      daily_cap: dailyCap,
      min_gap_seconds: minGapSeconds,
      jitter_seconds: jitterSeconds,
    });
    return response.data;
  }

  async listAccounts() {
    const response = await this.client.get('/api/outreach/accounts');
    return response.data;
  }

  async warmupOverview() {
    const response = await this.client.get('/api/outreach/accounts/warmup');
    return response.data;
  }

  async connectAccount({
    email, displayName, username, password,
    imapHost, imapPort, imapSecurity,
    smtpHost, smtpPort, smtpSecurity,
  }) {
    const response = await this.client.post('/api/outreach/accounts', {
      email,
      display_name: displayName,
      username,
      password,
      imap: { host: imapHost, port: imapPort, security: imapSecurity },
      smtp: { host: smtpHost, port: smtpPort, security: smtpSecurity },
    });
    return response.data;
  }

  async disconnectAccount(accountId) {
    await this.client.delete(`/api/outreach/accounts/${accountId}`);
  }

  async listInbox({
    classification,
    accountId,
    unreadOnly,
    starredOnly,
    waitingOnly,
    campaignRepliesOnly,
    q,
    limit = 50,
    offset = 0,
  } = {}) {
    const params = { limit, offset };
    if (classification) params.classification = classification;
    if (accountId) params.account_id = accountId;
    if (unreadOnly) params.unread_only = true;
    if (starredOnly) params.starred_only = true;
    if (waitingOnly) params.waiting_only = true;
    if (campaignRepliesOnly) params.campaign_replies_only = true;
    if (q) params.q = q;
    const response = await this.client.get('/api/outreach/inbox', { params });
    return response.data;
  }

  async getThread(threadId, accountId) {
    const response = await this.client.get(`/api/outreach/inbox/threads/${threadId}`, {
      params: accountId ? { account_id: accountId } : {},
    });
    return response.data;
  }

  async markMessagesRead(accountId, uids, read = true) {
    await this.client.post(`/api/outreach/inbox/${accountId}/messages/read`, { uids, read });
  }

  async setMessagesStarred(accountId, uids, starred) {
    await this.client.post(`/api/outreach/inbox/${accountId}/messages/star`, { uids, starred });
  }

  async getMessageBody(accountId, uid, folder) {
    const response = await this.client.get(`/api/outreach/inbox/${accountId}/messages/${uid}/body`, {
      params: { folder },
    });
    return response.data;
  }

  attachmentUrl(accountId, uid, index) {
    return `${OUTREACH_API_URL}/api/outreach/inbox/${accountId}/messages/${uid}/attachments/${index}`;
  }

  async downloadAttachment(accountId, uid, index, folder) {
    const response = await this.client.get(
      `/api/outreach/inbox/${accountId}/messages/${uid}/attachments/${index}`,
      { responseType: 'blob', params: { folder } }
    );
    return response.data;
  }

  async sendMessage(accountId, { to, subject, body, cc = [], files = [] }) {
    const form = new FormData();
    form.append('to', to);
    form.append('subject', subject);
    form.append('body', body);
    for (const address of cc) form.append('cc', address);
    for (const file of files) form.append('files', file);
    const response = await this.client.post(`/api/outreach/inbox/${accountId}/messages`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // By Message-ID: a cached uid can point at another message by send time.
  async setThreadHandled(threadId, handled, accountId) {
    const params = accountId ? { account_id: accountId } : {};
    const response = await this.client.post(
      `/api/outreach/inbox/threads/${threadId}/handled`,
      { handled },
      { params }
    );
    return response.data;
  }

  async replyToMessage(accountId, { messageId, folder, body, files = [], cc = [] }) {
    const form = new FormData();
    form.append('message_id', messageId);
    form.append('folder', folder);
    form.append('body', body);
    for (const address of cc) form.append('cc', address);
    for (const file of files) form.append('files', file);
    const response = await this.client.post(`/api/outreach/inbox/${accountId}/reply`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async dailyAnalytics(days) {
    // The viewer's own timezone, so "a day" on the chart is the day they live in.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await this.client.get('/api/outreach/analytics/daily', {
      params: { days, timezone },
    });
    return response.data;
  }

  async previewImport(file) {
    const form = new FormData();
    form.append('file', file);
    // The client defaults to application/json - without this override axios
    // JSON-stringifies the FormData into "{}" instead of sending the file.
    const response = await this.client.post('/api/outreach/contacts/import/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async startImport({ file, mapping, listName, listId, tags, dedupeAcrossLists }) {
    const form = new FormData();
    form.append('file', file);
    form.append('mapping', JSON.stringify(mapping));
    if (listName) form.append('list_name', listName);
    if (listId) form.append('list_id', listId);
    if (tags) form.append('tags', tags);
    if (dedupeAcrossLists) form.append('dedupe_across_lists', 'true');
    const response = await this.client.post('/api/outreach/contacts/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getImportJob(jobId) {
    const response = await this.client.get(`/api/outreach/contacts/import/${jobId}`);
    return response.data;
  }

  async listContacts({ listId, search, limit = 50, offset = 0 } = {}) {
    const params = { limit, offset };
    if (listId) params.list_id = listId;
    if (search) params.search = search;
    const response = await this.client.get('/api/outreach/contacts', { params });
    return response.data;
  }

  async listContactLists() {
    const response = await this.client.get('/api/outreach/contact-lists');
    return response.data;
  }

  async listCustomFieldKeys(listId) {
    const response = await this.client.get(`/api/outreach/contact-lists/${listId}/custom-field-keys`);
    return response.data;
  }

  async listSuppressions() {
    const response = await this.client.get('/api/outreach/suppressions');
    return response.data;
  }

  async addSuppression({ kind = 'email', value, reason = 'manual' }) {
    const response = await this.client.post('/api/outreach/suppressions', { kind, value, reason });
    return response.data;
  }

  async deleteSuppression(id) {
    await this.client.delete(`/api/outreach/suppressions/${id}`);
  }

  async verificationEstimate(listId) {
    const response = await this.client.get('/api/outreach/verification/estimate', {
      params: listId ? { list_id: listId } : {},
    });
    return response.data;
  }

  async runVerification(listId) {
    const response = await this.client.post('/api/outreach/verification/run', null, {
      params: listId ? { list_id: listId } : {},
    });
    return response.data;
  }

  async listCampaigns() {
    const response = await this.client.get('/api/outreach/campaigns');
    return response.data;
  }

  async getCampaign(id) {
    const response = await this.client.get(`/api/outreach/campaigns/${id}`);
    return response.data;
  }

  async createCampaign(payload) {
    const response = await this.client.post('/api/outreach/campaigns', payload);
    return response.data;
  }

  async enrollContacts(campaignId, { listId, contactIds = [] }) {
    const response = await this.client.post(`/api/outreach/campaigns/${campaignId}/enroll`, {
      list_id: listId || null,
      contact_ids: contactIds,
    });
    return response.data;
  }

  async updateCampaign(campaignId, changes) {
    const response = await this.client.patch(`/api/outreach/campaigns/${campaignId}`, changes);
    return response.data;
  }

  async setCampaignStatus(campaignId, newStatus) {
    const response = await this.client.post(
      `/api/outreach/campaigns/${campaignId}/status`,
      null,
      { params: { new_status: newStatus } }
    );
    return response.data;
  }

  async deleteCampaign(campaignId) {
    await this.client.delete(`/api/outreach/campaigns/${campaignId}`);
  }

  async campaignStats(campaignId) {
    const response = await this.client.get(`/api/outreach/campaigns/${campaignId}/stats`);
    return response.data;
  }

  async listLeads(params = {}) {
    const response = await this.client.get('/api/outreach/leads', { params });
    return response.data;
  }

  async leadBoard(params = {}) {
    const response = await this.client.get('/api/outreach/leads/board', { params });
    return response.data;
  }

  async leadsToday() {
    const response = await this.client.get('/api/outreach/leads/today');
    return response.data;
  }

  async getLead(leadId) {
    const response = await this.client.get(`/api/outreach/leads/${leadId}`);
    return response.data;
  }

  async setLeadStatus(leadId, { pipelineStatus, followUpAt = null, note = null }) {
    const response = await this.client.post(`/api/outreach/leads/${leadId}/status`, {
      pipeline_status: pipelineStatus,
      follow_up_at: followUpAt,
      note,
    });
    return response.data;
  }

  async resolveLead({ address, references }) {
    const response = await this.client.post('/api/outreach/leads/resolve', { address, references });
    return response.data;
  }

  async bulkSetLeadStatus({ pipelineStatus, followUpAt = null, note = null, leadIds = [], filters = null }) {
    const response = await this.client.post('/api/outreach/leads/bulk-status', {
      pipeline_status: pipelineStatus,
      follow_up_at: followUpAt,
      note,
      lead_ids: leadIds,
      filters,
    });
    return response.data;
  }

  async setLeadNotes(leadId, notes) {
    const response = await this.client.put(`/api/outreach/leads/${leadId}/notes`, { notes });
    return response.data;
  }

  async eraseLead(leadId) {
    const response = await this.client.delete(`/api/outreach/contacts/${leadId}`);
    return response.data;
  }

  async deleteContactList(listId) {
    await this.client.delete(`/api/outreach/contact-lists/${listId}`);
  }

  async listPipelineStatuses() {
    const response = await this.client.get('/api/outreach/pipeline-statuses');
    return response.data;
  }

  async createPipelineStatus(payload) {
    const response = await this.client.post('/api/outreach/pipeline-statuses', payload);
    return response.data;
  }

  async deletePipelineStatus(key) {
    await this.client.delete(`/api/outreach/pipeline-statuses/${key}`);
  }

  async workspaceAnalytics() {
    const response = await this.client.get('/api/outreach/analytics');
    return response.data;
  }

  async campaignAnalytics(campaignId) {
    const response = await this.client.get(`/api/outreach/analytics/campaigns/${campaignId}`);
    return response.data;
  }

  async spamCheckCampaign(campaignId, { stepIndex = 0, leadId = null } = {}) {
    const params = { step_index: stepIndex };
    if (leadId) params.lead_id = leadId;
    const response = await this.client.post(`/api/outreach/campaigns/${campaignId}/spam-check`, null, { params });
    return response.data;
  }

  async deliverabilityCheck(accountId) {
    const response = await this.client.post(`/api/outreach/accounts/${accountId}/deliverability-check`);
    return response.data;
  }

  async labelReply({ replyText, subject = null, leadId = null }) {
    const response = await this.client.post('/api/outreach/ai/label', {
      reply_text: replyText,
      subject,
      lead_id: leadId,
    });
    return response.data;
  }

  async draftReply({ threadText, senderName = null, instruction = null }) {
    const response = await this.client.post('/api/outreach/ai/draft', {
      thread_text: threadText,
      sender_name: senderName,
      instruction,
    });
    return response.data;
  }

  async listWebhooks() {
    const response = await this.client.get('/api/outreach/webhooks');
    return response.data;
  }

  async createWebhook(payload) {
    const response = await this.client.post('/api/outreach/webhooks', payload);
    return response.data;
  }

  async deleteWebhook(id) {
    await this.client.delete(`/api/outreach/webhooks/${id}`);
  }

  async testWebhook(id) {
    const response = await this.client.post(`/api/outreach/webhooks/${id}/test`);
    return response.data;
  }

  async webhookDeliveries(id) {
    const response = await this.client.get(`/api/outreach/webhooks/${id}/deliveries`);
    return response.data;
  }

  async listEventTypes() {
    const response = await this.client.get('/api/outreach/event-types');
    return response.data;
  }

  async listApiKeys() {
    const response = await this.client.get('/api/outreach/api-keys');
    return response.data;
  }

  async createApiKey(name) {
    const response = await this.client.post('/api/outreach/api-keys', { name });
    return response.data;
  }

  async revokeApiKey(id) {
    await this.client.delete(`/api/outreach/api-keys/${id}`);
  }

  async getWorkspaceSettings() {
    const response = await this.client.get('/api/outreach/workspace-settings');
    return response.data;
  }

  async updateWorkspaceSettings(payload) {
    const response = await this.client.patch('/api/outreach/workspace-settings', payload);
    return response.data;
  }

  async listChannels() {
    const response = await this.client.get('/api/outreach/channels');
    return response.data;
  }

  async myLimits() {
    const response = await this.client.get('/api/outreach/limits');
    return response.data;
  }

  async freezeSending(reason) {
    const response = await this.client.post('/api/outreach/limits/freeze', { reason });
    return response.data;
  }

  async unfreezeSending() {
    const response = await this.client.post('/api/outreach/limits/unfreeze');
    return response.data;
  }

  async deleteWorkspaceData() {
    const response = await this.client.delete('/api/outreach/workspace-data');
    return response.data;
  }

  async dispatchNow() {
    const response = await this.client.post('/api/outreach/campaigns/dispatch-now');
    return response.data;
  }
}

export const outreachApi = new OutreachApiService();
